import { useState, useEffect, useCallback } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Trophy, Medal, Award } from 'lucide-react'
import { getWeeklySalesPerformanceData } from '../../supabase-client'

export default function WeeklySalesPerformanceTable({ loading: externalLoading }) {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)

  const loadData = useCallback(async () => {
    if (externalLoading) return // Don't load if external data is being loaded

    setLoading(true)
    try {
      const weeklyData = await getWeeklySalesPerformanceData()
      setData(weeklyData)
    } catch (error) {
      console.error('Error loading weekly sales performance data:', error)
    } finally {
      setLoading(false)
    }
  }, [externalLoading])

  useEffect(() => {
    loadData()
  }, [loadData])

  const isLoading = externalLoading || loading

  // Get trophy icon for top 3 performing weeks
  const getRankIcon = (rank) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-4 w-4 text-yellow-500" />
      case 2:
        return <Medal className="h-4 w-4 text-gray-400" />
      case 3:
        return <Award className="h-4 w-4 text-orange-600" />
      default:
        return null
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value)
  }

  const formatASP = (asp) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(asp)
  }

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-12 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    )
  }

  // Reverse data so most recent week appears first
  const reversedData = [...data].reverse()

  // Sort data by mass to identify top 3 performing weeks
  const processedData = reversedData.map((item, index) => ({
    ...item,
    originalIndex: index
  }))

  // Create rankings based on mass performance
  const rankedData = [...processedData]
    .sort((a, b) => b.mass - a.mass)
    .map((item, index) => ({
      ...item,
      rank: index + 1
    }))
    .sort((a, b) => a.originalIndex - b.originalIndex)

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-2">
        New weeks starts Sun
      </div>
      
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="w-8"></TableHead>
              <TableHead className="text-left">Dates</TableHead>
              <TableHead className="text-left">Year</TableHead>
              <TableHead className="text-left">Month</TableHead>
              <TableHead className="text-left">Week</TableHead>
              <TableHead className="text-right">Mass (kg)</TableHead>
              <TableHead className="text-right">Value (£)</TableHead>
              <TableHead className="text-right">Target</TableHead>
              <TableHead className="text-right">ASP</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rankedData.map((item, index) => (
              <TableRow 
                key={index} 
                className="hover:bg-gray-50"
              >
                <TableCell className="w-8">
                  <div className="flex items-center justify-center">
                    {getRankIcon(item.rank)}
                  </div>
                </TableCell>
                <TableCell className="font-medium">{item.date}</TableCell>
                <TableCell>{item.year}</TableCell>
                <TableCell>{item.month}</TableCell>
                <TableCell>{item.week}</TableCell>
                <TableCell className="text-right font-medium">{item.mass}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(item.value)}</TableCell>
                <TableCell className="text-right">{item.target}</TableCell>
                <TableCell className="text-right">{formatASP(item.asp)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {data.length === 0 && !isLoading && (
        <div className="text-center text-gray-500 py-8">
          No weekly sales data available
        </div>
      )}
    </div>
  )
}